import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { UserIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import api from '@/services/api';
import { teamApi } from '@/services/teamApi';
import { contentApi, ContentType, AssigneeType } from '@/services/contentApi';

interface AssignModalProps {
  contentType: ContentType;
  contentId: string;
  onClose: () => void;
}

/** Picks a team (reusing the existing Teams feature) or a specific org member, and assigns the content item to them. */
const AssignModal: React.FC<AssignModalProps> = ({ contentType, contentId, onClose }) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<AssigneeType>('team');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: teamsRes } = useQuery({
    queryKey: ['teams-for-assign'],
    queryFn: () => teamApi.getAll({ isActive: true, limit: 100 }),
    enabled: tab === 'team',
  });
  const teams = teamsRes?.data?.data?.teams ?? [];

  const { data: usersRes } = useQuery({
    queryKey: ['users-for-assign'],
    queryFn: () => api.get<{ success: boolean; data: { users: Array<{ _id: string; name: string; email: string }> } }>('/users', { params: { limit: 100 } }),
    enabled: tab === 'user',
  });
  const users = usersRes?.data?.data?.users ?? [];

  const assignMutation = useMutation({
    mutationFn: () => contentApi.assign(contentType, contentId, tab, selectedId!),
    onSuccess: () => {
      toast.success('Assigned');
      queryClient.invalidateQueries({ queryKey: ['content-assignment', contentType, contentId] });
      onClose();
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to assign'),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Assign</h2>

        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
          <button
            onClick={() => { setTab('team'); setSelectedId(null); }}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 ${tab === 'team' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
          >
            <UserGroupIcon className="h-4 w-4" /> Team
          </button>
          <button
            onClick={() => { setTab('user'); setSelectedId(null); }}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 ${tab === 'user' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
          >
            <UserIcon className="h-4 w-4" /> Person
          </button>
        </div>

        <div className="max-h-64 overflow-y-auto space-y-1 mb-6">
          {tab === 'team'
            ? teams.map((team) => (
                <button
                  key={team._id}
                  onClick={() => setSelectedId(team._id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm ${selectedId === team._id ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white'}`}
                >
                  {team.name}
                  <span className="text-xs text-gray-400 ml-1.5">({team.members?.length ?? 0} members)</span>
                </button>
              ))
            : users.map((user) => (
                <button
                  key={user._id}
                  onClick={() => setSelectedId(user._id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm ${selectedId === user._id ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white'}`}
                >
                  {user.name}
                  <span className="text-xs text-gray-400 ml-1.5">{user.email}</span>
                </button>
              ))}
          {((tab === 'team' && teams.length === 0) || (tab === 'user' && users.length === 0)) && (
            <p className="text-sm text-gray-400 text-center py-6">Nothing to show here yet.</p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md">
            Cancel
          </button>
          <button
            onClick={() => assignMutation.mutate()}
            disabled={!selectedId || assignMutation.isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
          >
            {assignMutation.isLoading ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignModal;
